import { useEffect, useState } from 'react';
import { DealerAuthProvider, useDealerAuth } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerLayout from '../components/DealerLayout';

function AccountContent() {
  const { dealer, updateProfile, updatePassword } = useDealerAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    state: '',
    country: '',
    address: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (dealer) {
      setProfileForm({
        firstName: dealer.firstName,
        lastName: dealer.lastName,
        companyName: dealer.companyName,
        email: dealer.email,
        phone: dealer.phone,
        state: dealer.state,
        country: dealer.country,
        address: dealer.address,
      });
    }
  }, [dealer]);

  const card = dark ? 'bg-[#1C2B3A] border-white/5' : 'bg-white border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'w-full bg-[#0D1B2A] border border-white/10 text-white text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#F97316] placeholder-white/30 transition-colors'
    : 'w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#F97316] placeholder-gray-400 transition-colors';
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`;
  const avatarInitial = dealer?.contact?.charAt(0) ?? 'D';
  const updatePassBtn = dark
    ? 'bg-white/10 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm'
    : 'bg-gray-100 text-gray-700 font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm';

  return (
    <div className="space-y-6 max-w-2xl">
      <div className={`border rounded-lg p-6 ${card}`}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F97316] text-white text-xl font-black shrink-0">
            {avatarInitial}
          </div>
          <div>
            <p className={`font-black text-lg ${text}`}>{dealer?.contact}</p>
            <p className={`text-sm ${muted}`}>{dealer?.name}</p>
            <span className="inline-block mt-1 bg-green-500/20 text-green-400 text-xs font-bold px-2.5 py-0.5 rounded-full">
              Approved Dealer
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>First Name</label>
            <input
              value={profileForm.firstName}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, firstName: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Last Name</label>
            <input
              value={profileForm.lastName}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, lastName: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Company Name</label>
            <input
              value={profileForm.companyName}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, companyName: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Account Number</label>
            <input value={dealer?.accountNumber || ''} disabled className={`${inputCls} opacity-50 cursor-not-allowed`} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={profileForm.email} disabled className={`${inputCls} opacity-50 cursor-not-allowed`} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input
              type="tel"
              value={profileForm.phone}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>State</label>
            <input
              value={profileForm.state}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, state: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Country</label>
            <input
              value={profileForm.country}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, country: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className={labelCls}>Default Shipping Address</label>
          <textarea
            rows={2}
            value={profileForm.address}
            onChange={(e) => setProfileForm((prev) => ({ ...prev, address: e.target.value }))}
            className={`${inputCls} resize-none`}
          />
        </div>

        {profileError && (
          <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
            {profileError}
          </p>
        )}
        {profileMessage && (
          <p className="mt-4 text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3">
            {profileMessage}
          </p>
        )}

        <button
          onClick={async () => {
            setSavingProfile(true);
            setProfileError('');
            setProfileMessage('');
            const result = await updateProfile({
              firstName: profileForm.firstName,
              lastName: profileForm.lastName,
              companyName: profileForm.companyName,
              phone: profileForm.phone,
              state: profileForm.state,
              country: profileForm.country,
              address: profileForm.address,
            });
            setSavingProfile(false);
            if (result.ok) {
              setProfileMessage(result.message || 'Account details updated.');
            } else {
              setProfileError(result.error || 'Unable to update profile.');
            }
          }}
          disabled={savingProfile}
          className="mt-5 bg-[#F97316] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {savingProfile ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={`border rounded-lg p-6 ${card}`}>
        <h3 className={`font-bold text-sm mb-4 ${text}`}>Change Password</h3>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>New Password</label>
            <input
              type="password"
              placeholder="Minimum 8 characters"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input
              type="password"
              placeholder="Repeat your new password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              className={inputCls}
            />
          </div>
          {passwordError && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
              {passwordError}
            </p>
          )}
          {passwordMessage && (
            <p className="text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3">
              {passwordMessage}
            </p>
          )}
          <button
            onClick={async () => {
              setPasswordError('');
              setPasswordMessage('');

              if (passwordForm.newPassword.length < 8) {
                setPasswordError('Please choose a password with at least 8 characters.');
                return;
              }

              if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                setPasswordError('Passwords do not match.');
                return;
              }

              setSavingPassword(true);
              const result = await updatePassword(passwordForm.newPassword);
              setSavingPassword(false);

              if (result.ok) {
                setPasswordMessage(result.message || 'Password updated successfully.');
                setPasswordForm({ newPassword: '', confirmPassword: '' });
              } else {
                setPasswordError(result.error || 'Unable to update password.');
              }
            }}
            disabled={savingPassword}
            className={`${updatePassBtn} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {savingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DealerAccountPage() {
  return (
    <DealerAuthProvider>
      <DealerLayout title="My Account">
        <AccountContent />
      </DealerLayout>
    </DealerAuthProvider>
  );
}
